import { Notice } from '@rental/ui'
import type { ReactNode } from 'react'
import { useLocale } from '../i18n'
import type { StaffProblem } from '../problems'
import { PageHeader } from './PageHeader'
import { ArrivalNotice } from './Said'
import { Loading, ProblemNotice } from './ui'

/**
 * A screen this person may not open: its title and the refusal in words,
 * said after the title (UI-2g), and nothing it would have fetched. Not the
 * guard itself: the server refuses too.
 */
export function Guard({
  allowed,
  title,
  children,
}: {
  allowed: boolean
  title: ReactNode
  children: ReactNode
}) {
  const { messages: m } = useLocale()
  if (allowed) return children
  return (
    <>
      <PageHeader title={title} />
      <ArrivalNotice tone="warning">
        <p>{m.errors.codes.forbidden}</p>
      </ArrivalNotice>
    </>
  )
}

/**
 * A record's screen (a vehicle, a customer) before its record is in: the way
 * back and a title in every state, so there is always a heading to land on
 * (UI-2c). While it loads it says so; a record that is not there says so in
 * words; any other problem is said as a problem. The record's own header,
 * once it comes, is a new heading and takes the focus: its name is read out,
 * not changed in silence.
 */
export function RecordPending({
  back,
  title,
  problem,
  notFound,
}: {
  back: ReactNode
  title: ReactNode
  problem: StaffProblem | null
  /** Said when the address names no such record: an id unknown (404) or not one at all (400). */
  notFound: ReactNode
}) {
  const missing = problem?.kind === 'api' && (problem.status === 404 || problem.status === 400)
  return (
    <>
      <PageHeader back={back} title={title}>
        {problem ? null : <Loading />}
      </PageHeader>
      {missing ? (
        // Said as it comes, as a problem is (UI-2g): it takes the loading line's place, and
        // a notice drawn in silence there left the title alone, with nothing under it read.
        <Notice tone="warning" live="assertive">
          <p>{notFound}</p>
        </Notice>
      ) : (
        <ProblemNotice problem={problem} />
      )}
    </>
  )
}

/**
 * What a query has to show: its answer once it has one, a problem in words
 * whenever there is one (a refresh that fails keeps the last answer), and
 * that it is loading until then, on a pane like every other word. Inside a
 * pane already there (`bare`, a Section's), on that pane: its own would be a
 * pane in a pane (UI-2g).
 */
export function LoadState<T>({
  data,
  problem,
  bare = false,
  children,
}: {
  data: T | undefined
  problem: StaffProblem | null
  /** Drawn inside a pane: the loading line needs none of its own. */
  bare?: boolean
  children: (data: T) => ReactNode
}) {
  return (
    <>
      <ProblemNotice problem={problem} />
      {data !== undefined ? (
        children(data)
      ) : problem ? null : bare ? (
        <Loading />
      ) : (
        <div className="glass-pane rounded-card px-4 sm:px-6">
          <Loading />
        </div>
      )}
    </>
  )
}
